import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL } from "../api";
import { getImageUrl } from "../utils/imageUrl";
import MobileHeader from "../mobile/MobileHeader";
import { COLOR, FONT, RADIUS, SHADOW, EASE, GlobalMobileStyles, Eyebrow } from "../mobile/mobiletheme";
import { NAV_HEIGHT } from "../mobile/MobileBottomNav";

const MIN_ORDER_VALUE = 799;
const LOW_STOCK_THRESHOLD = 5;

export default function MobileCart({ user_id, refresh, onCartChange, onBack }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const getName = (item) => {
    let name = item?.name;
    if (typeof name === "string") { 
      try { 
        const parsed = JSON.parse(name); 
        if (parsed && typeof parsed === "object") name = parsed; 
      } catch (_) {} 
    }
    if (typeof name === "string") return name;
    
    const lang = (i18n.language || "en").split("-")[0];
    return name?.[lang] || name?.en || name?.hi || name?.te || "";
  };

  const [cartItems, setCartItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [recommendations, setRecommendations] = useState([]);
  const [checkingOut, setCheckingOut] = useState(false);
  const [pendingIds, setPendingIds] = useState({});
  const pendingIdsRef = useRef({});
  useEffect(() => { pendingIdsRef.current = pendingIds; }, [pendingIds]);

  const [search, setSearch] = useState("");
  const [headerHeight, setHeaderHeight] = useState(112);
  const [step, setStep] = useState(1);
  const contentAreaRef = useRef(null);

  const [contact, setContact] = useState({
    fullName: "",
    phone: "",
    altPhone: "",
    building: "",
    street: "",
    landmark: "",
    city: "",
    postalCode: "",
    deliveryDate: "",
    deliverySlot: "",
  });

  const DELIVERY_SLOTS = ["9:00 AM - 12:00 PM", "12:00 PM - 3:00 PM", "3:00 PM - 6:00 PM", "6:00 PM - 9:00 PM"];
  const DELIVERY_SLOT_KEYS = {
    "9:00 AM - 12:00 PM": "mobile.slotMorning",
    "12:00 PM - 3:00 PM": "mobile.slotNoon",
    "3:00 PM - 6:00 PM": "mobile.slotAfternoon",
    "6:00 PM - 9:00 PM": "mobile.slotEvening",
  };
  const todayISO = new Date().toISOString().split("T")[0];

  const [error, setError] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(true);

  const [locating, setLocating] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [myAddresses, setMyAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);

  const loadMyAddresses = async () => {
    if (!user_id || user_id === "guest") return;
    setAddressesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/address/${encodeURIComponent(user_id)}`);
      if (res.data.success) {
        setMyAddresses(res.data.addresses || []);
      }
    } catch (err) {
      console.error("Failed to load saved addresses:", err);
    } finally {
      setAddressesLoading(false);
    }
  };

  useEffect(() => {
    loadMyAddresses();
  }, [user_id]);

  const cartFetchSeq = useRef(0);

  const fetchCart = async () => {
    const seq = ++cartFetchSeq.current;
    try {
      const res = await axios.get(`${API_URL}/cart/${user_id}`);
      if (seq !== cartFetchSeq.current) return;
      if (res.data?.success) {
        const rawItems = res.data.cart || [];
        const grouped = rawItems.reduce((acc, item) => {
          const key = `${item.product_id}-${item.category}`;
          acc[key] = acc[key] ? { ...acc[key], quantity: acc[key].quantity + Number(item.quantity) } : { ...item };
          return acc;
        }, {});

        const processed = Object.values(grouped).reverse();
        const pending = pendingIdsRef.current;
        const currentById = new Map(cartItems.map((i) => [i.id, i]));
        const reconciled = processed.map((item) =>
          pending[item.id] && currentById.has(item.id) ? currentById.get(item.id) : item
        );

        setCartItems(reconciled);
        setTotal(reconciled.reduce((acc, item) => acc + Number(item.price) * Number(item.quantity), 0));
      }
    } catch (err) { console.error("Fetch cart error:", err); }
  };

  const fetchRecommendations = async () => {
    try {
      const res = await axios.get(`${API_URL}/cart/recommendations/all`);
      setRecommendations(res.data.products || []);
    } catch (err) {
      console.error("Fetch recommendations error:", err);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [refresh, user_id]);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const goSearch = () => {
    if (search.trim() !== "") navigate(`/search?q=${encodeURIComponent(search.trim())}`);
  };

  const handleContactChange = (e) => {
    let val = e.target.value;
    if (e.target.name === "phone" || e.target.name === "altPhone") {
      val = val.replace(/\D/g, "").slice(0, 10);
    }
    setContact({ ...contact, [e.target.name]: val });

    if (["building", "street", "landmark", "city", "postalCode"].includes(e.target.name)) {
      setSelectedAddressId(null);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return alert(t("mobile.geoNotSupported"));
    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
          const res = await axios.get(url);

          if (res.data && res.data.address) {
            const addr = res.data.address;
            setContact(prev => ({
              ...prev,
              building: addr.house_number || addr.building || "",
              street: addr.road || addr.residential || addr.suburb || "",
              landmark: addr.neighbourhood || addr.village || addr.county || "",
              city: addr.city || addr.town || addr.state_district || "",
              postalCode: addr.postcode || ""
            }));
            setSelectedAddressId(null);
          } else {
            alert(t("mobile.geoFoundNoAddress"));
          }
        } catch (err) {
          console.error(err);
          alert(t("mobile.geoFetchError"));
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        console.error(err);
        alert(t("mobile.geoPermissionError"));
        setLocating(false);
      }
    );
  };

  const TrashIcon = ({ size = 18, color = COLOR.error }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );

  const PinIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={COLOR.primaryDark} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );

  const ArrowIcon = ({ size = 15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={COLOR.onPrimary} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="13 6 19 12 13 18" />
    </svg>
  );

  const addToCart = async (item) => {
    const pendingKey = `add-${item.id}`;
    if (pendingIds[pendingKey]) return;
    setPendingIds((p) => ({ ...p, [pendingKey]: true }));

    const category = item.category || "vegetables";
    const tempId = `temp-${item.id}-${category}`;
    setCartItems((prev) => {
      const next = [...prev, { id: tempId, product_id: item.id, category, name: getName(item), price: item.price, image: item.image, quantity: 1 }];
      setTotal(next.reduce((acc, i) => acc + Number(i.price) * Number(i.quantity), 0));
      return next;
    });

    try {
      const res = await axios.post(`${API_URL}/cart/add`, { user_id, product_id: item.id, category });
      const real = res.data?.cartItem;
      setCartItems((prev) => {
        const next = prev.map((i) => (i.id === tempId
          ? { ...i, id: real ? real.id : i.id, quantity: real ? real.quantity : i.quantity }
          : i));
        setTotal(next.reduce((acc, x) => acc + Number(x.price) * Number(x.quantity), 0));
        return next;
      });
      onCartChange?.();
    } catch (err) {
      console.error("Add to cart error:", err);
      setCartItems((prev) => {
        const next = prev.filter((i) => i.id !== tempId);
        setTotal(next.reduce((acc, i) => acc + Number(i.price) * Number(i.quantity), 0));
        return next;
      });
    } finally {
      setPendingIds((p) => {
        const next = { ...p };
        delete next[pendingKey];
        return next;
      });
    }
  };

  const updateQty = async (id, action, currentQty) => {
    if (pendingIds[id]) return; 
    const willRemove = action === "decrease" && currentQty <= 1;
    setPendingIds((p) => ({ ...p, [id]: true }));

    setCartItems((prev) => {
      const next = willRemove
        ? prev.filter((item) => item.id !== id)
        : prev.map((item) =>
            item.id === id
              ? { ...item, quantity: action === "increase" ? item.quantity + 1 : item.quantity - 1 }
              : item
          );
      setTotal(next.reduce((acc, item) => acc + Number(item.price) * Number(item.quantity), 0));
      return next;
    });

    try {
      if (willRemove) await axios.delete(`${API_URL}/cart/${id}`);
      else await axios.put(`${API_URL}/cart/${action}/${id}`);
      onCartChange?.();
    } catch (err) {
      console.error("Update quantity error:", err);
      await fetchCart(); 
    } finally {
      setPendingIds((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
    }
  };

  const removeItem = async (id) => {
    if (pendingIds[id]) return;
    setPendingIds((p) => ({ ...p, [id]: true }));
    setCartItems((prev) => {
      const next = prev.filter((item) => item.id !== id);
      setTotal(next.reduce((acc, item) => acc + Number(item.price) * Number(item.quantity), 0));
      return next;
    });
    try {
      await axios.delete(`${API_URL}/cart/${id}`);
      onCartChange?.();
    } catch (err) {
      console.error("Remove item error:", err);
      await fetchCart();
    } finally {
      setPendingIds((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
    }
  };

  const hasOutOfStockItem = cartItems.some((item) => Number(item.stock) <= 0);
  const hasStockIssue = cartItems.some((item) => {
    const s = Number(item.stock);
    return s <= 0 || item.quantity > s;
  });

  // Display-only ordering: sink out-of-stock items to the bottom of the
  // cart list so the items someone can actually check out with stay up
  // top. Totals, min-order checks, etc. above are order-independent, so
  // they keep using the raw cartItems. Array.prototype.sort is stable,
  // so everything else keeps its existing relative order.
  const displayCartItems = useMemo(() => {
    return [...cartItems].sort((a, b) => {
      const aOut = Number(a.stock) <= 0 ? 1 : 0;
      const bOut = Number(b.stock) <= 0 ? 1 : 0;
      return aOut - bOut;
    });
  }, [cartItems]);

  const isBelowMinOrder = (cartItems.length > 0 ? total : 0) < MIN_ORDER_VALUE;
  const amountToReachMin = MIN_ORDER_VALUE - total;

  const handlePlaceOrderClick = () => {
    if (cartItems.length === 0) return;
    if (hasStockIssue) {
      setError(t("mobile.cartStockError"));
      return;
    }
    if (isBelowMinOrder) {
      setError(t("mobile.minOrder", { min: MIN_ORDER_VALUE, amount: amountToReachMin.toFixed(0) }));
      return;
    }
    setStep(2);
    setTimeout(() => {
      if (contentAreaRef.current) contentAreaRef.current.scrollTop = 0;
      window.scrollTo(0, 0);
    }, 10);
  };

  const handleBackClick = () => {
    if (step === 2) {
      setStep(1);
      setTimeout(() => {
        if (contentAreaRef.current) contentAreaRef.current.scrollTop = 0;
        window.scrollTo(0, 0);
      }, 10);
    } else {
      if (onBack) onBack();
    }
  };

  const grandTotal = cartItems.length > 0 ? total : 0;

  const handleCheckout = async () => {
    if (
      !contact.fullName || contact.phone.length !== 10 || contact.altPhone.length !== 10 ||
      !contact.building || !contact.street || !contact.city || !contact.postalCode
    ) {
      setError(t("mobile.fillAllFields"));
      return;
    }
    if (!contact.deliveryDate || !contact.deliverySlot) {
      setError(t("mobile.chooseDateTime"));
      return;
    }
    if (grandTotal < MIN_ORDER_VALUE) {
      setError(t("mobile.minOrderBack", { min: MIN_ORDER_VALUE }));
      return;
    }

    setError("");
    setCheckingOut(true);

    try {
      const payload = {
        user_id,
        items: cartItems,
        total_price: grandTotal,
        contact,
        saveAsDefault,
        isNewAddress: !selectedAddressId
      };
      const res = await axios.post(`${API_URL}/cart/checkout`, payload);

      if (res.data.success) {
        setOrderSuccess(true);
        if (onCartChange) onCartChange();
        loadMyAddresses();
      } else {
        setError(res.data.message || t("mobile.failedPlaceOrder"));
      }
    } catch (err) {
      setError(err.response?.data?.message || t("mobile.checkoutError"));
    } finally {
      setCheckingOut(false);
    }
  };

  const totalItemCount = cartItems.reduce((acc, item) => acc + Number(item.quantity), 0);

  const getCartItem = (productId, category) => {
    return cartItems.find(
      (c) => (c.product_id === productId && (!category || c.category === category)) || c.id === productId
    );
  };

  const renderRecCard = (item) => {
    const cartItem = getCartItem(item.id, item.category);
    const recStock = Number(item.stock);
    const hasStockInfo = item.stock !== undefined && item.stock !== null && !isNaN(recStock);
    const recOutOfStock = hasStockInfo && recStock <= 0;
    const recLowStock = hasStockInfo && !recOutOfStock && recStock <= LOW_STOCK_THRESHOLD;
    const recAtLimit = !!cartItem && hasStockInfo && recStock > 0 && cartItem.quantity >= recStock;
    const noPrice = item.price === undefined || item.price === null || Number(item.price) <= 0;
    return (
      <div key={`${item.category}-${item.id}`} style={recOutOfStock ? { ...styles.recCard, ...styles.recCardOutOfStock } : styles.recCard}>
        <div style={styles.recImgWrapper}>
          <img
            src={getImageUrl(item.image)}
            style={recOutOfStock ? { ...styles.recImg, ...styles.recImgOutOfStock } : styles.recImg}
            alt={getName(item)}
            loading="lazy"
          />
          {recOutOfStock && <div style={styles.recOutOfStockBadge}>{t("cart.outOfStock")}</div>}
          {recLowStock && <div style={styles.recLowStockBadge}>{recStock === 1 ? t("mobile.left", { count: 1 }) : t("mobile.left", { count: recStock })}</div>}
        </div>
        <p style={styles.gridTitle}>{getName(item)}</p>
        <span style={styles.gridUnit}>{item.unit || "1 kg"}</span>

        <div style={styles.priceRow}>
          <p style={styles.price}>{noPrice ? "—" : `₹${item.price}`}</p>
          {recOutOfStock ? (
            <span style={styles.outOfStockText}>{t("cart.outOfStock")}</span>
          ) : noPrice ? (
            <span style={styles.outOfStockText}>{t("mobile.unavailable")}</span>
          ) : cartItem && cartItem.quantity > 0 ? (
            <div style={styles.recQtyBox}>
              <button style={styles.recBtn} className="sb-tap" disabled={!!pendingIds[cartItem.id]} onClick={() => updateQty(cartItem.id, "decrease", cartItem.quantity)}>−</button>
              <span style={styles.recQty}>{cartItem.quantity}</span>
              <button style={styles.recBtn} className="sb-tap" disabled={!!pendingIds[cartItem.id] || recAtLimit} title={recAtLimit ? `Only ${recStock} in stock` : undefined} onClick={() => updateQty(cartItem.id, "increase", cartItem.quantity)}>+</button>
            </div>
          ) : (
            <button style={styles.addBtn} className="sb-tap" disabled={!!pendingIds[`add-${item.id}`]} onClick={() => addToCart(item)}>
              {pendingIds[`add-${item.id}`] ? "..." : t("mobile.add")}
            </button>
          )}
        </div>
      </div>
    );
  };

  const recRow1 = recommendations.filter((_, i) => i % 2 === 0);
  const recRow2 = recommendations.filter((_, i) => i % 2 === 1);

  if (orderSuccess) {
    return (
      <div style={styles.container}>
        <GlobalMobileStyles />
        <MobileHeader
          showLogo={false}
          showTitleBar
          showBackButton
          onBack={onBack}
          title={t("cart.orderSuccess").toUpperCase()}
          onHeightChange={setHeaderHeight}
        />

        <div style={{ ...styles.successWrapper, paddingTop: `${headerHeight}px` }}>
          <svg width="72" height="72" viewBox="0 0 64 64" style={{ animation: "sb-pop 0.45s cubic-bezier(0.22,1,0.36,1)" }}>
            <circle cx="32" cy="32" r="30" fill={COLOR.accentSoft} />
            <path d="M20 33 L28 41 L45 24" fill="none" stroke={COLOR.accent} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <h2 style={styles.successTitle}>{t("cart.orderSuccess")}</h2>
          <p style={{ color: COLOR.muted, margin: "8px 0 10px" }}>{t("cart.received")}</p>
          {contact.deliveryDate && contact.deliverySlot && (
            <p style={{ color: COLOR.ink, fontWeight: "700", margin: "0 0 22px" }}>
              {t("cart.scheduledFor")} {new Date(`${contact.deliveryDate}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}, {contact.deliverySlot}
            </p>
          )}
          <button style={styles.checkoutBtn} className="sb-tap" onClick={onBack}>
            {t("cart.backToHome")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <GlobalMobileStyles />
      <MobileHeader
        searchValue={search}
        setSearchValue={setSearch}
        onSearch={goSearch}
        showLogo={false}
        showTitleBar
        showBackButton
        onBack={handleBackClick}
        title={step === 2 ? t("cart.deliveryDetails").toUpperCase() : t("cart.myCart").toUpperCase()}
        isLoggedIn={user_id && user_id !== "guest"}
        onHeightChange={setHeaderHeight}
      />

      <div
        ref={contentAreaRef}
        style={{ ...styles.contentArea, paddingTop: `${headerHeight + 15}px` }}
      >
        {step === 2 ? (
          <>
            <div style={styles.noteCard}>
              <Eyebrow tone="primary">{t("cart.importantInfo").toUpperCase()}</Eyebrow>
              <p style={styles.noteText}>{t("cart.note1")}</p>
              <p style={styles.noteText}>{t("cart.note2")}</p>
              <div style={styles.noteContactBox}>
                <span style={styles.noteContactLabel}>{t("cart.supportContact")}</span>
                <span style={styles.noteContactNumber}>📞 6301912803</span>
              </div>
            </div>

            <div style={styles.formContainer}>
              <div style={styles.instructionBanner}>
                <p style={styles.instructionText}>
                  👉 <b>{t("cart.instruction")}</b>
                </p>
              </div>

              <h3 style={styles.sectionTitle}>{t("cart.contactInfo")}</h3>
              <input type="text" name="fullName" value={contact.fullName} onChange={handleContactChange} placeholder={t("cart.fullName")} style={styles.input} className="sb-field" />

              <div style={{ display: "flex", gap: "8px" }}>
                <div style={styles.phoneInputWrapper} className="sb-field">
                  <span style={styles.prefix}>+91</span>
                  <input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} placeholder={t("cart.phone")} maxLength="10" style={styles.phoneInput} />
                </div>
                <div style={styles.phoneInputWrapper} className="sb-field">
                  <span style={styles.prefix}>+91</span>
                  <input type="tel" name="altPhone" value={contact.altPhone} onChange={handleContactChange} placeholder={t("cart.altPhone")} maxLength="10" style={styles.phoneInput} />
                </div>
              </div>

              <h3 style={styles.sectionTitle}>{t("cart.deliveryAddress")}</h3>

              <button type="button" className="sb-tap" onClick={handleUseCurrentLocation} style={styles.detectBtn} disabled={locating}>
                <PinIcon size={13} /> {locating ? t("cart.detecting") : t("cart.detectLocation")}
              </button>

              <input type="text" name="building" value={contact.building} onChange={handleContactChange} placeholder={t("cart.building")} style={styles.input} className="sb-field" />
              <input type="text" name="street" value={contact.street} onChange={handleContactChange} placeholder={t("cart.street")} style={styles.input} className="sb-field" />
              <input type="text" name="landmark" value={contact.landmark} onChange={handleContactChange} placeholder={t("cart.landmark")} style={styles.input} className="sb-field" />

              <div style={{ display: "flex", gap: "8px" }}>
                <input type="text" name="city" value={contact.city} onChange={handleContactChange} placeholder={t("cart.city")} style={{ ...styles.input, flex: 1 }} className="sb-field" />
                <input type="text" name="postalCode" value={contact.postalCode} onChange={handleContactChange} placeholder={t("cart.pincode")} style={{ ...styles.input, flex: 1 }} className="sb-field" />
              </div>

              <h3 style={styles.sectionTitle}>{t("cart.deliveryDateTime")}</h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="date"
                  name="deliveryDate"
                  value={contact.deliveryDate}
                  min={todayISO}
                  onChange={handleContactChange}
                  style={{ ...styles.input, flex: 1 }}
                  className="sb-field"
                />
                <select
                  name="deliverySlot"
                  value={contact.deliverySlot}
                  onChange={handleContactChange}
                  style={{ ...styles.input, flex: 1 }}
                  className="sb-field"
                >
                  <option value="">{t("cart.selectTimeSlot")}</option>
                  {DELIVERY_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>{t(DELIVERY_SLOT_KEYS[slot])}</option>
                  ))}
                </select>
              </div>

              {(addressesLoading || myAddresses.length > 0) && (
                <div style={{ marginTop: "18px", borderTop: `1px dashed ${COLOR.line}`, paddingTop: "14px" }}>
                  <p style={styles.savedAddressesHeading}>
                    {addressesLoading ? t("cart.loadingAddresses") : t("cart.savedAddresses")}
                  </p>
                  {myAddresses.map((addr) => (
                    <div
                      key={addr.id}
                      className="sb-tap"
                      style={{
                        ...styles.savedAddressCard,
                        borderColor: selectedAddressId === addr.id ? COLOR.primary : COLOR.line,
                        backgroundColor: selectedAddressId === addr.id ? COLOR.primarySoft : COLOR.paper
                      }}
                      onClick={() => {
                        setContact(prev => ({
                          ...prev,
                          fullName: addr.full_name || prev.fullName,
                          phone: addr.phone ? String(addr.phone).replace(/\D/g, "").slice(-10) : prev.phone,
                          altPhone: addr.alt_phone ? String(addr.alt_phone).replace(/\D/g, "").slice(-10) : prev.altPhone,
                          building: addr.building || prev.building,
                          street: addr.street || prev.street,
                          landmark: addr.landmark || prev.landmark,
                          city: addr.city || prev.city,
                          postalCode: addr.postal_code || prev.postalCode,
                        }));
                        setSelectedAddressId(addr.id);
                      }}
                    >
                      <div style={styles.savedAddressHeader}>
                        <span style={styles.savedBadge}>{addr.label || t("mobile.savedAddress")}{addr.is_default ? ` • ${t("mobile.default")}` : ""}</span>
                        <span style={styles.tapToUse}>{selectedAddressId === addr.id ? t("mobile.selected") : t("mobile.tapToUse")}</span>
                      </div>
                      <p style={styles.savedText}>
                        <strong>{addr.full_name}</strong><br/>
                        {addr.building}, {addr.street}<br/>
                        {addr.city} - {addr.postal_code}<br/>
                        Phone: {addr.phone}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <label style={styles.checkboxRow}>
                <input type="checkbox" checked={saveAsDefault} onChange={(e) => setSaveAsDefault(e.target.checked)} style={styles.checkbox} />
                {t("cart.saveAsDefault")}
              </label>

              {error && <p style={styles.errorText}>{error}</p>}
            </div>
          </>
        ) : (
        <>
        {cartItems.length === 0 ? (
          <div style={styles.emptyCart}>
            <div style={styles.emptyIconWrap}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={COLOR.muted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </div>
            <h3 style={styles.emptyTitle}>{t("cart.emptyCart")}</h3>
            <p style={styles.emptyText}>{t("cart.emptyText")}</p>
            <button style={styles.browseBtn} className="sb-tap" onClick={onBack}>{t("cart.browseItems")}</button>
          </div>
        ) : (
          <>
            <div style={styles.cartCountRow}>
              <Eyebrow tone="primary">
                {totalItemCount === 1 
                  ? t("cart.itemsInCart_one", { count: totalItemCount }) 
                  : t("cart.itemsInCart_other", { count: totalItemCount })}
              </Eyebrow>
            </div>

            <div style={styles.itemsListCard}>
              {displayCartItems.map((item, idx) => {
                const isPending = !!pendingIds[item.id];
                const stockNum = Number(item.stock);
                const outOfStock = stockNum <= 0;
                const exceedsStock = !outOfStock && item.quantity > stockNum;
                const lowStock = !outOfStock && !exceedsStock && stockNum <= LOW_STOCK_THRESHOLD;
                const atStockLimit = !outOfStock && item.quantity >= stockNum;
                const hasIssue = outOfStock || exceedsStock;
                const lineTotal = (Number(item.price) * Number(item.quantity)).toFixed(0);
                const isLast = idx === displayCartItems.length - 1;
                return (
                  <div
                    key={item.id}
                    style={{
                      ...styles.itemRow,
                      ...(isLast ? { borderBottom: "none" } : {}),
                      ...(hasIssue ? styles.itemRowIssue : {}),
                      opacity: isPending ? 0.6 : hasIssue ? 0.85 : 1,
                    }}
                  >
                    <div style={styles.itemImgWrapper}>
                      <img src={getImageUrl(item.image)} style={outOfStock ? { ...styles.itemImg, filter: "grayscale(1)" } : styles.itemImg} alt={getName(item)} />
                      {outOfStock && <div style={styles.itemBadgeDark}>{t("cart.outOfStock")}</div>}
                      {lowStock && <div style={styles.itemBadgePop}>{stockNum === 1 ? t("mobile.left", { count: 1 }) : t("mobile.left", { count: stockNum })}</div>}
                    </div>
                    <div style={styles.itemInfo}>
                      <div style={styles.itemHeader}>
                        <div style={{ flex: 1, paddingRight: "8px" }}>
                          <h4 style={styles.itemTitle}>{getName(item)}</h4>
                          <p style={styles.itemWeight}>{item.unit || "1 kg"}</p>
                          {exceedsStock && (
                            <p style={styles.outOfStockText}>{t("cart.onlyLeft", { count: stockNum })}</p>
                          )}
                        </div>
                        <button style={styles.ghostTrashBtn} className="sb-tap" onClick={() => removeItem(item.id)} aria-label={`Remove ${getName(item)}`}>
                          <TrashIcon size={16} color={COLOR.muted} />
                        </button>
                      </div>
                      <div style={styles.itemBottom}>
                        <div style={styles.priceBlock}>
                          <span style={styles.itemUnitPrice}>₹{item.price} {t("cart.each")}</span>
                          <span style={styles.priceText}>₹{lineTotal}</span>
                        </div>
                        <div style={styles.qtyPill}>
                          <button style={styles.qtyPillBtn} className="sb-tap" disabled={isPending} onClick={() => updateQty(item.id, "decrease", item.quantity)}>−</button>
                          <span style={styles.qtyPillText}>{item.quantity}</span>
                          <button
                            style={{ ...styles.qtyPillBtn, ...(atStockLimit ? styles.qtyBtnDisabled : {}) }}
                            className="sb-tap"
                            disabled={isPending || outOfStock || atStockLimit}
                            title={atStockLimit ? `Only ${stockNum} in stock` : undefined}
                            onClick={() => updateQty(item.id, "increase", item.quantity)}
                          >+</button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {cartItems.length > 0 && (
          <div style={styles.billContainer}>
            <Eyebrow tone="accent">{t("cart.billDetails")}</Eyebrow>
            <div style={{ ...styles.row, marginTop: "14px" }}>
              <p style={styles.billLabel}>{t("cart.itemTotal")}</p>
              <p style={styles.billValue}>₹{total}</p>
            </div>
            <div style={styles.row}>
              <p style={styles.billLabel}>{t("cart.deliveryFee")}</p>
              <p style={styles.billValue}>₹ --</p>
            </div>
            <div style={styles.row}>
              <p style={styles.billLabel}>{t("cart.handlingCharge")}</p>
              <p style={styles.billValue}>₹ --</p>
            </div>
            <hr style={styles.divider} />
            <div style={styles.row}>
              <p style={styles.grandTotalLabel}>{t("cart.grandTotal")}</p>
              <p style={styles.grandTotalValue}>₹{grandTotal}</p>
            </div>
          </div>
        )}
        {error && step === 1 && <p style={styles.errorText}>{error}</p>}

        {recommendations.length > 0 && (
          <div style={{ margin: "26px 0" }}>
            <h4 style={styles.sectionTitle}>{t("cart.youMightLike")}</h4>

            <div style={styles.recRow} className="mc-rec-scroll">
              {recRow1.map((item) => renderRecCard(item))}
            </div>
            {recRow2.length > 0 && (
              <div style={{ ...styles.recRow, marginTop: "12px" }} className="mc-rec-scroll">
                {recRow2.map((item) => renderRecCard(item))}
              </div>
            )}
          </div>
        )}
        </>
        )}
      </div>

      {cartItems.length > 0 && (
        <div style={styles.floatBar}>
          <div style={styles.checkoutCard}>
            {step === 1 && isBelowMinOrder && (
              <div style={styles.progressTrack}>
                <div
                  style={{ ...styles.progressFill, width: `${Math.min(100, (grandTotal / MIN_ORDER_VALUE) * 100)}%` }}
                />
                <span style={styles.progressText}>
                  {t("cart.addMore", { amount: amountToReachMin.toFixed(0), min: MIN_ORDER_VALUE })}
                </span>
              </div>
            )}

            <div style={styles.checkoutRow}>
              <div style={styles.checkoutPriceBlock}>
                <span style={styles.checkoutItemCount}>
                  {totalItemCount === 1
                    ? t("nav.itemCount_one", { count: totalItemCount })
                    : t("nav.itemCount_other", { count: totalItemCount })}
                </span>
                <span style={styles.checkoutTotal}>₹{grandTotal}</span>
              </div>

              <div style={styles.checkoutDivider} />

              {step === 1 ? (
                <button
                  style={{ ...styles.checkoutCta, opacity: isBelowMinOrder ? 0.55 : 1, cursor: isBelowMinOrder ? "not-allowed" : "pointer" }}
                  className="sb-tap"
                  onClick={handlePlaceOrderClick}
                  disabled={isBelowMinOrder}
                >
                  {t("cart.proceedToCheckout")}
                  <ArrowIcon />
                </button>
              ) : (
                <button style={{ ...styles.checkoutCta, opacity: checkingOut ? 0.7 : 1 }} className="sb-tap" onClick={handleCheckout} disabled={checkingOut}>
                  {checkingOut ? t("cart.placing") : t("cart.confirmOrder")}
                  {!checkingOut && <ArrowIcon />}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: COLOR.mist,
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    fontFamily: FONT.body,
  },

  contentArea: {
    flex: 1,
    width: "100%",
    height: "100%",
    padding: `0 16px ${120 + NAV_HEIGHT}px 16px`,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    boxSizing: "border-box"
  },

  noteCard: {
    backgroundColor: COLOR.paper,
    border: `1px solid ${COLOR.line}`,
    borderRadius: RADIUS.lg,
    padding: "16px",
    marginBottom: "16px",
    boxShadow: SHADOW.soft,
  },
  noteText: {
    margin: "10px 0 10px 0",
    fontSize: "clamp(12px, 3.5vw, 14px)",
    color: COLOR.ink,
    lineHeight: "1.5"
  },
  noteContactBox: {
    backgroundColor: COLOR.primarySoft,
    borderRadius: RADIUS.md,
    padding: "10px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: `1px solid ${COLOR.line}`,
    marginTop: "4px",
  },
  noteContactLabel: {
    fontSize: "12px",
    color: COLOR.primaryDark,
    fontWeight: "600"
  },
  noteContactNumber: {
    fontSize: "14px",
    fontWeight: "800",
    color: COLOR.primaryDark,
  },

  instructionBanner: {
    backgroundColor: COLOR.popSoft,
    borderLeft: `4px solid ${COLOR.pop}`,
    padding: "12px 14px",
    marginBottom: "18px",
    borderRadius: "6px"
  },
  instructionText: {
    margin: 0,
    fontSize: "13px",
    color: COLOR.warning,
    lineHeight: "1.5"
  },

  successWrapper: { display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: 1, minHeight: "calc(100vh - 100px)", padding: "20px", textAlign: "center", backgroundColor: COLOR.mist },
  successTitle: { fontFamily: FONT.display, fontSize: "clamp(20px, 5vw, 24px)", color: COLOR.ink, margin: "18px 0 0" },

  cartCountRow: { marginBottom: "12px" },

  emptyCart: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "48px 20px 32px" },
  emptyIconWrap: { width: "72px", height: "72px", borderRadius: "50%", background: COLOR.paper, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px", border: `1px solid ${COLOR.line}`, boxShadow: SHADOW.soft },
  emptyTitle: { fontFamily: FONT.display, fontSize: "clamp(16px, 4.5vw, 18px)", color: COLOR.ink, margin: "0 0 6px" },
  emptyText: { fontSize: "13px", color: COLOR.muted, margin: "0 0 20px" },
  browseBtn: { background: COLOR.gradientCTA, color: COLOR.onPrimary, border: "none", padding: "12px 26px", borderRadius: RADIUS.pill, fontWeight: "800", fontSize: "14px", cursor: "pointer", boxShadow: SHADOW.cta, fontFamily: FONT.body },

  itemsListCard: { display: "flex", flexDirection: "column" },
  itemRow: { display: "flex", padding: "14px 0", alignItems: "center", position: "relative", borderBottom: `1px solid ${COLOR.line}` },
  itemRowIssue: { backgroundColor: COLOR.errorSoft },
  itemImgWrapper: { position: "relative", width: "84px", height: "84px", flexShrink: 0, borderRadius: RADIUS.md, overflow: "hidden", backgroundColor: COLOR.mist },
  itemImg: { width: "100%", height: "100%", objectFit: "cover" },
  itemBadgeDark: { position: "absolute", top: "4px", left: "4px", backgroundColor: "#0f172a", color: "#ffffff", fontSize: "8.5px", fontWeight: "bold", padding: "2px 5px", borderRadius: "4px", textTransform: "uppercase", letterSpacing: "0.2px" },
  itemBadgePop: { position: "absolute", top: "4px", left: "4px", backgroundColor: COLOR.pop, color: COLOR.onPrimary, fontSize: "8.5px", fontWeight: "bold", padding: "2px 5px", borderRadius: "4px" },
  itemInfo: { marginLeft: "14px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: "84px" },
  itemHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  itemTitle: { fontSize: "clamp(14px, 4vw, 16px)", margin: 0, fontWeight: "700", color: COLOR.ink, lineHeight: "1.4", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontFamily: FONT.body },
  itemWeight: { fontSize: "12.5px", color: COLOR.muted, margin: "4px 0 0 0", fontWeight: "500" },
  outOfStockText: { fontSize: "11px", fontWeight: "bold", color: COLOR.error, margin: "4px 0 0 0" },
  lowStockText: { fontSize: "11px", fontWeight: "bold", color: COLOR.pop, margin: "4px 0 0 0" },
  qtyBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  ghostTrashBtn: { background: "transparent", border: "none", padding: "4px", borderRadius: RADIUS.sm, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  itemBottom: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "10px" },
  priceBlock: { display: "flex", flexDirection: "column", gap: "1px" },
  itemUnitPrice: { fontSize: "11px", color: COLOR.muted, fontWeight: "500" },
  priceText: { fontSize: "clamp(14px, 4vw, 16px)", fontWeight: "800", color: COLOR.ink, margin: 0, fontFamily: FONT.body },

  qtyPill: { display: "flex", alignItems: "center", justifyContent: "center", gap: "2px", background: COLOR.primary, borderRadius: "5px", padding: "2px 4px", boxShadow: SHADOW.soft },
  qtyPillBtn: { width: "26px", height: "26px", border: "none", background: "transparent", color: COLOR.onPrimary, fontWeight: "bold", fontSize: "16px", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  qtyPillText: { minWidth: "20px", textAlign: "center", fontSize: "14px", fontWeight: "700", color: COLOR.onPrimary },

  billContainer: { backgroundColor: "transparent", padding: "20px 0", marginTop: "12px", borderTop: `1px solid ${COLOR.line}` },
  divider: { border: "none", borderTop: `1px dashed ${COLOR.line}`, margin: "15px 0" },
  row: { display: "flex", justifyContent: "space-between", margin: "10px 0", fontSize: "14px" },
  billLabel: { margin: 0, color: COLOR.muted },
  billValue: { margin: 0, color: COLOR.ink, fontWeight: "600" },
  grandTotalLabel: { margin: 0, fontWeight: "800", fontSize: "clamp(14px, 4vw, 16px)", color: COLOR.ink },
  grandTotalValue: { margin: 0, fontWeight: "800", fontSize: "clamp(14px, 4vw, 16px)", color: COLOR.ink, fontFamily: FONT.body },

  formContainer: { background: "transparent", padding: "16px 0", borderTop: `1px solid ${COLOR.line}` },
  sectionTitle: { fontSize: "clamp(13px, 4vw, 15px)", fontWeight: "800", marginBottom: "8px", marginTop: "14px", color: COLOR.ink, letterSpacing: "0.2px", fontFamily: FONT.display },

  detectBtn: { width: "100%", padding: "11px", background: COLOR.primarySoft, color: COLOR.primaryDark, border: `1px solid ${COLOR.primarySoft}`, borderRadius: RADIUS.md, fontWeight: "700", cursor: "pointer", marginBottom: "10px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "12.5px", gap: "7px" },

  input: { width: "100%", padding: "11px 12px", marginTop: "6px", marginBottom: "6px", borderRadius: RADIUS.md, border: `1.5px solid ${COLOR.line}`, boxSizing: "border-box", fontSize: "13px", background: COLOR.mist, color: COLOR.ink, transition: "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease", fontFamily: "inherit" },

  phoneInputWrapper: { display: "flex", alignItems: "center", border: `1.5px solid ${COLOR.line}`, borderRadius: RADIUS.md, background: COLOR.mist, overflow: "hidden", marginTop: "6px", marginBottom: "6px", flex: 1, transition: "border-color 0.15s ease, box-shadow 0.15s ease" },
  prefix: { padding: "11px 10px", background: COLOR.surface, color: COLOR.muted, fontWeight: "700", borderRight: `1px solid ${COLOR.line}`, fontSize: "12.5px" },
  phoneInput: { flex: 1, padding: "11px 10px", border: "none", outline: "none", fontSize: "13px", background: "transparent", color: COLOR.ink },

  savedAddressesHeading: { fontSize: "12px", fontWeight: "800", color: COLOR.muted, margin: "0 0 8px" },
  savedAddressCard: { padding: "12px", border: `1.5px solid ${COLOR.line}`, borderRadius: RADIUS.md, backgroundColor: COLOR.paper, cursor: "pointer", transition: "all 0.2s ease", marginBottom: "8px", boxShadow: "0 1px 4px rgba(0,0,0,0.02)" },
  savedAddressHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" },
  savedBadge: { fontSize: "9px", fontWeight: "800", color: COLOR.ink, backgroundColor: COLOR.mist, padding: "4px 6px", borderRadius: "4px", textTransform: "uppercase", letterSpacing: "0.5px" },
  tapToUse: { fontSize: "11px", color: COLOR.primaryDark, fontWeight: "800" },
  savedText: { margin: 0, fontSize: "12px", color: COLOR.muted, lineHeight: "1.4" },

  checkboxRow: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", color: COLOR.ink, marginTop: "14px", cursor: "pointer", fontWeight: "700" },
  checkbox: { width: "15px", height: "15px", cursor: "pointer", accentColor: COLOR.primary },
  errorText: { color: COLOR.error, fontSize: "12px", marginTop: "12px", fontWeight: "600", background: COLOR.errorSoft, padding: "10px 12px", borderRadius: RADIUS.md },

  recRow: { display: "flex", gap: "12px", overflowX: "auto", overflowY: "hidden", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none", paddingBottom: "4px" },
  recCard: { width: "148px", flexShrink: 0, backgroundColor: "transparent", padding: "0 8px", display: "flex", flexDirection: "column", minWidth: 0, overflow: "visible", boxSizing: "border-box" },
  recCardOutOfStock: { opacity: 0.6 },
  recImgWrapper: { width: "100%", aspectRatio: "1", overflow: "hidden", borderRadius: RADIUS.md, marginBottom: "8px", backgroundColor: COLOR.mist, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" },
  recImg: { width: "100%", height: "100%", objectFit: "cover" },
  recImgOutOfStock: { filter: "grayscale(1)" },
  recOutOfStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: "#0f172a", color: "#ffffff", fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px", textTransform: "uppercase", letterSpacing: "0.2px" },
  recLowStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: COLOR.pop, color: COLOR.onPrimary, fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px", letterSpacing: "0.2px" },

  gridTitle: { fontSize: "14px", fontWeight: "700", color: COLOR.ink, margin: "8px 0 4px 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  gridUnit: { fontSize: "12px", color: COLOR.muted, margin: "0 0 12px 0", fontWeight: "500", display: "block" },

  priceRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" },
  price: { fontWeight: "900", fontSize: "clamp(14px, 4vw, 16px)", color: COLOR.ink, margin: 0, fontFamily: FONT.body },

  addBtn: { padding: "3px 10px", backgroundColor: COLOR.paper, color: COLOR.primaryDark, border: `1.5px solid ${COLOR.primaryDark}`, borderRadius: RADIUS.sm, fontWeight: "700", fontSize: "12px", letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" },

  recQtyBox: { display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", backgroundColor: COLOR.primaryDark, border: `1.5px solid ${COLOR.primaryDark}`, borderRadius: RADIUS.sm, padding: "1px 3px", height: "30px", boxSizing: "border-box" },

  recBtn: { border: "none", background: "none", color: COLOR.onPrimary, fontWeight: "bold", cursor: "pointer", fontSize: "16px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  recQty: { fontSize: "14px", fontWeight: "700", color: COLOR.onPrimary, minWidth: "14px", textAlign: "center" },

  floatBar: {
    position: "fixed", left: 0, right: 0, bottom: `${NAV_HEIGHT - 70  }px`, zIndex: 998,
    padding: "0 14px calc(6px + env(safe-area-inset-bottom, 0px))",
    boxSizing: "border-box",
    pointerEvents: "none",
  },
  checkoutCard: {
    pointerEvents: "auto",
    background: COLOR.paper,
    borderRadius: RADIUS.lg,
    boxShadow: SHADOW.float,
    border: `1px solid ${COLOR.line}`,
    overflow: "hidden",
    animation: "sb-pop 0.3s ease",
  },
  progressTrack: {
    position: "relative",
    height: "24px",
    background: COLOR.mist,
    borderBottom: `1px solid ${COLOR.line}`,
    overflow: "hidden",
  },
  progressFill: {
    position: "absolute", top: 0, left: 0, bottom: 0,
    background: COLOR.primarySoft,
    borderRight: `2px solid ${COLOR.primary}`,
    transition: `width 0.4s ${EASE}`,
  },
  progressText: {
    position: "relative", zIndex: 1,
    display: "flex", alignItems: "center", justifyContent: "center",
    height: "100%", width: "100%",
    fontFamily: FONT.body, fontSize: "10.5px", fontWeight: 700,
    color: COLOR.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    padding: "0 10px", boxSizing: "border-box",
  },
  checkoutRow: {
    display: "flex", alignItems: "center", gap: "12px",
    padding: "10px 12px",
  },
  checkoutPriceBlock: {
    display: "flex", flexDirection: "column", gap: "1px", flexShrink: 0,
  },
  checkoutItemCount: {
    fontFamily: FONT.body, fontSize: "9px", fontWeight: 700,
    letterSpacing: "0.06em", textTransform: "uppercase", color: COLOR.muted,
  },
  checkoutTotal: {
    fontFamily: FONT.body, fontWeight: "800", fontSize: "17px", color: COLOR.ink,
  },
  checkoutDivider: {
    width: "1px", alignSelf: "stretch", background: COLOR.line, flexShrink: 0,
  },
  checkoutCta: {
    flex: 1,
    background: COLOR.gradientCTA, color: COLOR.onPrimary, border: "none",
    borderRadius: RADIUS.md, fontWeight: "800", fontSize: "14.5px", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
    padding: "12px 16px",
    boxShadow: SHADOW.cta,
    fontFamily: FONT.body,
  },
  checkoutBtn: { background: COLOR.gradientCTA, color: COLOR.onPrimary, border: "none", padding: "14px 28px", borderRadius: RADIUS.lg, fontWeight: "800", fontSize: "15px", cursor: "pointer", boxShadow: SHADOW.cta, fontFamily: FONT.body },
};

if (typeof document !== "undefined" && !document.getElementById("mc-rec-scroll-kf")) {
  const styleTag = document.createElement("style");
  styleTag.id = "mc-rec-scroll-kf";
  styleTag.textContent = `.mc-rec-scroll::-webkit-scrollbar { display: none; height: 0; }`;
  document.head.appendChild(styleTag);
}